import React from 'react';
import { X, Lightbulb, Info, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * TipBox component for displaying contextual tips on different pages
 * @param {string} pageId - Unique identifier for the page (e.g., 'workspace-detail', 'basket-content')
 * @param {string} tipId - Unique identifier for the specific tip (e.g., 'search-tip', 'filter-tip')
 * @param {string|ReactNode} message - The tip message to display (can be string or JSX for emphasis)
 * @param {string|ReactNode} title - Optional title for the tip (displayed prominently for pro tips)
 * @param {string} type - Type of tip: 'info', 'warning', 'success', 'tip' (default), 'pro'
 * @param {string} position - Position: 'top', 'bottom' (default: 'top')
 * @param {boolean} dismissible - Whether the tip can be dismissed (default: true)
 * @param {boolean} showAgainButton - Whether to show "Show Pro Tips" button when dismissed (default: false)
 * @param {Function} onDismiss - Callback when tip is dismissed
 * @param {Object} customData - Additional data to include in localStorage key
 * @param {string} className - Additional CSS classes
 */
const TipBox = ({
  pageId,
  tipId,
  message,
  title,
  type = 'tip',
  position = 'top',
  dismissible = true,
  showAgainButton = false,
  onDismiss,
  customData = {},
  className = ''
}) => {
  const storageKey = `tipbox_${pageId}_${tipId}_${JSON.stringify(customData)}`;
  const [isVisible, setIsVisible] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    const dismissed = localStorage.getItem(storageKey);
    return dismissed !== 'true';
  });

  const handleDismiss = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(storageKey, 'true');
    }
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleShowAgain = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(storageKey);
    }
    setIsVisible(true);
  };

  // Show "Show Pro Tips" button if dismissed and showAgainButton is enabled
  if (!isVisible && showAgainButton) {
    return (
      <div className={`${position === 'top' ? 'mt-0 mb-3 sm:mb-4' : 'mb-4 mt-0'} mx-2 sm:mx-0 flex justify-end`}>
        <button
          onClick={handleShowAgain}
          className="text-sm text-[#8B4513] hover:text-[#6B3410] underline transition-colors duration-200"
          aria-label="Show pro tips again"
        >
          Show Pro Tips
        </button>
      </div>
    );
  }

  if (!isVisible || !message) return null;

  const typeConfig = {
    tip: {
      bg: 'bg-butter-50',
      border: 'border-butter-400',
      text: 'text-neutral-900',
      icon: Lightbulb,
      iconColor: 'text-butter-600'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-400',
      text: 'text-neutral-900',
      icon: Info,
      iconColor: 'text-blue-600'
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-400',
      text: 'text-neutral-900',
      icon: AlertCircle,
      iconColor: 'text-yellow-600'
    },
    success: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-400',
      text: 'text-neutral-900',
      icon: CheckCircle,
      iconColor: 'text-emerald-600'
    },
    pro: {
      bg: 'bg-gradient-to-r from-yellow-50 to-amber-50',
      border: 'border-l-4 border-[#8B4513] border border-black',
      text: 'text-neutral-900',
      icon: Lightbulb,
      iconColor: 'text-[#8B4513]'
    }
  };

  const config = typeConfig[type] || typeConfig.tip;
  const Icon = config.icon;

  return (
    <div
      className={`
        ${config.bg} 
        ${position === 'top' ? 'mt-2 mb-2 sm:mb-3' : 'mb-2 mt-2 sm:mt-3'} 
        mx-2 sm:mx-0 
        p-2 sm:p-3 
        rounded-lg 
        shadow-sm 
        relative 
        border-l-4 
        ${config.border}
        ${className}
      `}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-2">
        <Icon 
          className={`${config.iconColor} flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 mt-0.5`} 
          aria-hidden="true"
        />
        <div className="flex-grow pr-6">
          {/* Pro Tip Header or Title - for pro type, use title if provided, otherwise show "Pro Tip" */}
          {type === 'pro' && (
            <>
              <div className={`font-bold text-sm sm:text-base mb-1 ${config.text}`}>
                {title || 'Pro Tip'}
              </div>
              <div className="border-b border-black mb-2"></div>
            </>
          )}
          
          {/* Tip Title - if provided and not pro type */}
          {title && type !== 'pro' && (
            <div className={`font-semibold text-sm sm:text-base mb-1 ${config.text}`}>
              {title}
            </div>
          )}
          
          {/* Tip Message */}
          <div className={`${config.text} text-xs sm:text-sm leading-relaxed`}>
            {message}
          </div>
        </div>
        {dismissible && (
          <button
            onClick={handleDismiss}
            className={`
              absolute top-1.5 right-1.5 
              ${config.text} 
              hover:opacity-70 
              transition-opacity 
              duration-200
              p-0.5
              rounded-lg
              hover:bg-white/50
            `}
            aria-label="Dismiss tip"
          >
            <X size={16} className="sm:w-4 sm:h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default TipBox;

